// "Add staging environment" — enter a manually-created SiteGround staging copy for this site.
import { useState } from 'react';
import type { FormEvent } from 'react';
import { getEnvironment } from '../../lib/env';
import { useAddStaging } from './hooks';

const ID = 'em-mgr--site--add-staging';

const FIELD_CLS =
  'w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100';

export function AddStagingForm({ siteId }: { siteId: string }) {
  const [open, setOpen] = useState(false);
  const [wpUrl, setWpUrl] = useState('');
  const [sshUser, setSshUser] = useState('');
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('18765');
  const [remoteWpPath, setRemoteWpPath] = useState('');
  const [wpUser, setWpUser] = useState('');
  const [wpPassword, setWpPassword] = useState('');
  const addStaging = useAddStaging(siteId);

  // Staging is a REMOTE (production-side) environment — a local dev site has no
  // staging copy, so only offer this on the production manager, never manage.convenely.local.
  if (getEnvironment() !== 'production') {
    return null;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    addStaging.mutate({
      wpUrl,
      sshUser,
      sshHost,
      sshPort: Number(sshPort),
      remoteWpPath,
      ...(wpUser && wpPassword ? { wpUser, wpPassword } : {}),
    });
  }

  return (
    <div data-id={ID} className="rounded border border-slate-200 p-3 dark:border-slate-800">
      <button
        type="button"
        data-id={`${ID}--toggle`}
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
      >
        {open ? '− Add staging environment' : '+ Add staging environment'}
      </button>

      {open && (
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="text-slate-500">Staging URL</span>
            <input
              data-id={`${ID}--wp-url`}
              className={FIELD_CLS}
              value={wpUrl}
              onChange={(e) => setWpUrl(e.target.value)}
              placeholder="https://staging5.mythmaker.ca"
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">SSH user</span>
            <input
              data-id={`${ID}--ssh-user`}
              className={FIELD_CLS}
              value={sshUser}
              onChange={(e) => setSshUser(e.target.value)}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">SSH host</span>
            <input
              data-id={`${ID}--ssh-host`}
              className={FIELD_CLS}
              value={sshHost}
              onChange={(e) => setSshHost(e.target.value)}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">SSH port</span>
            <input
              data-id={`${ID}--ssh-port`}
              className={FIELD_CLS}
              value={sshPort}
              onChange={(e) => setSshPort(e.target.value)}
              inputMode="numeric"
              required
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-slate-500">Remote WP path (absolute)</span>
            <input
              data-id={`${ID}--remote-path`}
              className={FIELD_CLS}
              value={remoteWpPath}
              onChange={(e) => setRemoteWpPath(e.target.value)}
              placeholder="/home/u1/www/staging5.mythmaker.ca/public_html"
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">WP user (optional, for registry)</span>
            <input
              data-id={`${ID}--wp-user`}
              className={FIELD_CLS}
              value={wpUser}
              onChange={(e) => setWpUser(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-500">WP App Password (optional)</span>
            <input
              data-id={`${ID}--wp-password`}
              className={FIELD_CLS}
              type="password"
              value={wpPassword}
              onChange={(e) => setWpPassword(e.target.value)}
            />
          </label>

          <div className="flex items-center gap-3 sm:col-span-2">
            <button
              type="submit"
              data-id={`${ID}--submit`}
              disabled={addStaging.isPending}
              className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
            >
              {addStaging.isPending ? 'Adding…' : 'Add staging'}
            </button>
            {addStaging.isSuccess && (
              <span data-id={`${ID}--success`} className="text-sm text-green-700 dark:text-green-400">
                Added {addStaging.data.environment}
                {addStaging.data.registered ? ' (registry credential stored)' : ''}
              </span>
            )}
            {addStaging.isError && (
              <span data-id={`${ID}--error`} className="text-sm text-red-600 dark:text-red-400">
                {addStaging.error instanceof Error ? addStaging.error.message : 'Add failed'}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
